// The Fleet page's ONE Send work controller (#6770, epic #6552): which card's form is open
// (one per page, the prototype's single `S.sendOpen` / `S.sendSec`, `:1381,2656`), the send
// itself, and the tasks this page sent, followed until their machine claims them. Built
// once in `routes/Fleet.tsx` and handed down to every card.
//
// THE SENT IDS ARE ONE SIGNAL, EACH READ OWNED BY ITS ID. A `mapArray` over the signal calls
// `useTaskDetail` once per id (`createLiveQuery`, live on the `task` entity, 60 s
// `refreshMs`), so a read is disposed the moment its id leaves the list: once the card has
// said the task went back to the open queue (`acknowledge`), once its machine claimed it,
// or once it moved past the claim in any other status (`sentSettled`). No poll of its own:
// #6767's release sweep bumps `tasks.updated_at`, so a release re-reads at once.
//
// THE SERVER IS THE ARBITER. A 201 adds the minted task as it was answered (Starting until
// its read says otherwise) and announces it; a refusal is thrown back to the form, which
// keeps its fields and shows the reason (`refusalLine`).
//
// EVERY ANSWER BELONGS TO THE FORM INSTANCE THAT SENT IT (#7686 review). Each open mints a
// `SendFormInstance`; the form captures its own at mount and hands it to `send`, which compares
// it with the instance open NOW when the answer lands, whatever the answer is (201, refusal,
// network error). Only that instance is closed by its 201 or shown its refusal. Once it is gone
// (closed, another card's form opened, or this card's form reopened with a new draft), a 201
// still adds its Starting row and announcement, and a refusal or failure becomes a note on the
// card it was sent to (`notes`), beside the back-to-the-queue note, so it is never swallowed and
// never painted into a form that did not send it.

import { parseWorkLink } from '@developerz/domain';
import { type Accessor, createEffect, createMemo, createSignal, mapArray } from 'solid-js';
import { t } from '../i18n';
import { type BoxSendBody, sendBoxWork } from './box-lifecycle';
import type { TaskRow } from './fleet';
import type { UtilBox } from './fleetUtilization';
import { boxJobs } from './machine-jobs';
import {
  refusalLine,
  releasedTasks,
  releaseNote,
  type SentTask,
  type StartingRow,
  sentAnnouncement,
  sentSettled,
  startingRows,
} from './send-work';
import { useTaskDetail } from './tasks';

/** One opening of one card's form; a reopen, even of the same card, is a new instance. */
export type SendFormInstance = { readonly boxId: string; readonly n: number };

/** What `send` hands back to the form that called it. */
export type SendOutcome =
  /** Minted; the form, if it is still the open one, has been closed. */
  | { readonly kind: 'sent' }
  /** Refused or failed while the sending form is still open: it shows this in its line. */
  | { readonly kind: 'refused'; readonly line: string }
  /** The sending form is gone: a refusal or failure went to the card's notes instead. */
  | { readonly kind: 'noted' };

/** A note the card shows once for 7 s: a release back to the queue, or a late refusal. */
export type CardNote = { readonly id: string; readonly message: string };

/** What every card reads and calls; `components/machine-card/SendWork.tsx` is its form. */
export type SendWorkPage = {
  /** The machine whose form is open, or null: at most one on the page. */
  readonly openId: Accessor<string | null>;
  /** The open form's instance; the form captures its own at mount. */
  readonly instance: Accessor<SendFormInstance | null>;
  /** Open this machine's form, closing any other; a no-op while it is already open. */
  readonly open: (boxId: string) => void;
  readonly close: () => void;
  /** Send from one form instance; every answer is judged against the instance open now. */
  readonly send: (
    box: UtilBox,
    body: BoxSendBody,
    idempotencyKey: string,
    from: SendFormInstance,
  ) => Promise<SendOutcome>;
  /** The card's Starting rows. */
  readonly starting: (box: UtilBox) => readonly StartingRow[];
  /** The card's notes not yet said: releases back to the queue, then late refusals. */
  readonly notes: (boxId: string) => readonly CardNote[];
  /** The card said it: stop following the task (disposing its read), or drop the note. */
  readonly acknowledge: (id: string) => void;
};

export type SendWorkPageDeps = {
  readonly token: string | null;
  /** The page's live task queue (`useTaskQueue(token).rows`). */
  readonly queue: () => readonly TaskRow[];
  /** The page's announcement: "PR #N sent to <Role> on <machine>". */
  readonly notify: (message: string) => void;
  /** The send itself; `sendBoxWork` unless a test injects one. */
  readonly sender?: typeof sendBoxWork;
};

export function createSendWorkPage(deps: SendWorkPageDeps): SendWorkPage {
  const sender = deps.sender ?? sendBoxWork;
  const [instance, setInstance] = createSignal<SendFormInstance | null>(null);
  let opened = 0;
  const [late, setLate] = createSignal<readonly (CardNote & { readonly boxId: string })[]>([]);
  const [sent, setSent] = createSignal<readonly SentTask[]>([]);
  // One live read per sent id, created and disposed with its entry.
  const reads = createMemo(
    mapArray(sent, (s) => {
      const read = useTaskDetail(() => s.id, deps.token);
      return (): SentTask => (read.data === undefined ? s : { ...s, row: read.data });
    }),
  );
  const followed = createMemo((): readonly SentTask[] => reads().map((r) => r()));
  const drop = (ids: ReadonlySet<string>): void => {
    setSent((prev) =>
      prev.some((s) => ids.has(s.id)) ? prev.filter((s) => !ids.has(s.id)) : prev,
    );
  };
  // A task its machine claimed, or one past the claim, is no longer this form's to follow.
  createEffect(() => {
    const done = new Set(
      followed()
        .filter(sentSettled)
        .map((s) => s.id),
    );
    if (done.size > 0) drop(done);
  });

  /** Close the form only if `from` is still the open instance. */
  const closeIfOpen = (from: SendFormInstance): void => {
    setInstance((open) => (open === from ? null : open));
  };

  return {
    openId: () => instance()?.boxId ?? null,
    instance,
    open: (boxId) => {
      if (instance()?.boxId === boxId) return;
      opened += 1;
      setInstance({ boxId, n: opened });
    },
    close: () => setInstance(null),
    send: async (box, body, idempotencyKey, from) => {
      const link = parseWorkLink(body.link);
      let task: TaskRow;
      try {
        task = await sender(box.id, body, idempotencyKey, deps.token);
      } catch (err) {
        const line = refusalLine(err, link, t);
        if (instance() === from) return { kind: 'refused', line };
        setLate((prev) => [...prev, { id: `late-${from.n}`, boxId: box.id, message: line }]);
        return { kind: 'noted' };
      }
      if (link !== null) {
        const entry: SentTask = {
          id: task.id,
          boxId: box.id,
          kind: link.kind,
          number: link.number,
          row: task,
        };
        setSent((prev) => (prev.some((s) => s.id === task.id) ? prev : [...prev, entry]));
        deps.notify(sentAnnouncement(link, body.role, box.display_name, t));
      }
      closeIfOpen(from);
      return { kind: 'sent' };
    },
    starting: (box) =>
      startingRows(
        box.id,
        deps.queue(),
        followed(),
        boxJobs(box).map((j) => j.id),
      ),
    notes: (boxId) => [
      ...releasedTasks(boxId, followed()).map((s) => ({ id: s.id, message: releaseNote(s, t) })),
      ...late().filter((n) => n.boxId === boxId),
    ],
    acknowledge: (id) => {
      drop(new Set([id]));
      setLate((prev) => (prev.some((n) => n.id === id) ? prev.filter((n) => n.id !== id) : prev));
    },
  };
}
